/**
 * The survey question editor's words for the validator's codes (plan-030 WAPP-Q; design §41.6.2, §41.6.3): every
 * problem and warning code the api answers has an English and a Greek sentence in `survey-questionnaire.{en,el}.json`,
 * filled from the note's `detail`. The api's own `message` is English for API clients; the portal never shows it for a
 * code it knows, and shows it only for a code a newer api answers that this console does not know yet.
 */
import { QUESTIONNAIRE_PROBLEM_CODE, QUESTIONNAIRE_WARNING_CODE } from "@wetalk/domain";

import { portalLocale, portalText, type PortalKey } from "../../../../i18n/portal.js";
import type { QuestionnaireNoteView, QuestionnaireQuestionType } from "../../../../api/slice/survey-questionnaire.js";

type CopyKey = PortalKey<"surveyQuestionnaire">;

/** A phrase of the editor's catalog (`survey-questionnaire.{en,el}.json`). */
export function editorText(key: CopyKey, values?: Readonly<Record<string, string | number>>): string {
  return portalText("surveyQuestionnaire", key, values);
}

const text = editorText;

/** A question type in words: "Scale", "Choice", "Open answer", "Number". */
export function questionTypeLabel(type: QuestionnaireQuestionType): string {
  switch (type) {
    case "scale":
      return text("type.scale");
    case "choice":
      return text("type.choice");
    case "open":
      return text("type.open");
    case "number":
      return text("type.number");
  }
}

/** A language's name in the portal's language ("Greek" / «Ελληνικά»), or its code when the browser has no name. */
export function languageLabel(languageCode: string): string {
  try {
    const name = new Intl.DisplayNames([portalLocale() === "el" ? "el" : "en"], { type: "language" }).of(languageCode);
    return name === undefined || name === "" ? languageCode : name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return languageCode;
  }
}

/** A group's name: "Group 1" for `A`, "Group 2" for `B`. */
export function groupLabel(variant: string): string {
  return text("group.name", { number: variant === "B" ? 2 : 1 });
}

function detailText(note: QuestionnaireNoteView, key: string): string {
  const value = note.detail[key];
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

const QUESTION_TYPE: readonly string[] = ["scale", "choice", "open", "number"];

function typeWord(raw: string): string {
  return QUESTION_TYPE.includes(raw) ? questionTypeLabel(raw as QuestionnaireQuestionType).toLowerCase() : raw;
}

/** Every value a sentence may name, read from the note's detail. */
function valuesOf(note: QuestionnaireNoteView): Readonly<Record<string, string>> {
  const variant = detailText(note, "variant");
  const language = detailText(note, "language");
  return {
    limit: detailText(note, "limit"),
    // `count` is i18next's plural option, so the detail's count travels as `total`.
    total: detailText(note, "count"),
    length: detailText(note, "length"),
    ordinal_count: detailText(note, "ordinal_count"),
    question_code: detailText(note, "question_code"),
    free_code: detailText(note, "free_code"),
    history_type: typeWord(detailText(note, "history_type")),
    group: variant === "" ? "" : groupLabel(variant),
    language: language === "" ? "" : languageLabel(language),
  };
}

/** A problem in the portal's language. */
export function problemText(note: QuestionnaireNoteView): string {
  const known = (QUESTIONNAIRE_PROBLEM_CODE as readonly string[]).includes(note.code);
  if (!known) return note.message;
  return text(`problem.${note.code}` as CopyKey, valuesOf(note));
}

/** A warning in the portal's language. */
export function warningText(note: QuestionnaireNoteView): string {
  const known = (QUESTIONNAIRE_WARNING_CODE as readonly string[]).includes(note.code);
  if (!known) return note.message;
  return text(`warning.${note.code}` as CopyKey, valuesOf(note));
}
